import * as React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ChevronsUpDown } from "lucide-react";
import { usersListOptions } from "@/api/users/query-options";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

export interface AdminWorker {
  id: string;
  name: string;
}

export interface AdminWorkerPickerProps {
  value: AdminWorker | null;
  onChange: (v: AdminWorker | null) => void;
  className?: string;
}

export function AdminWorkerPicker({ value, onChange, className }: AdminWorkerPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");

  const { data } = useQuery(usersListOptions({ q: q || undefined, pageSize: 20 }));

  const items = data?.data ?? [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex w-full min-h-9 items-center gap-1.5 rounded-4 border border-line-strong bg-surface px-2 py-1.5 text-left",
            "transition-colors hover:border-ink-300",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-p-600 focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
            className,
          )}
        >
          <span className={cn("flex-1 truncate text-sm", value ? "text-ink-900" : "text-ink-500")}>
            {value?.name ?? t("admin.uren.insert.worker-placeholder")}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 text-ink-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t("admin.uren.insert.worker-search")}
            value={q}
            onValueChange={setQ}
          />
          <CommandList>
            <CommandEmpty>{t("admin.uren.insert.worker-empty")}</CommandEmpty>
            {items.length > 0 && (
              <CommandGroup>
                {items.map((u) => (
                  <CommandItem
                    key={u.id}
                    value={u.id}
                    onSelect={() => {
                      onChange({ id: u.id, name: u.name });
                      setOpen(false);
                      setQ("");
                    }}
                  >
                    {u.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
